import type { AssignmentRow } from '../components/shared/assignments-card/assignments-card.component';
import { AssignmentBaseDto } from '../models/request.models';
import { PracticeAreaDto } from '../models/resource.models';

/**
 * What every assignment DTO shares that a row reads: the base DTO plus the type pair and
 * practice areas both subclasses declare. Structural, so MatterAssignmentDto and
 * EmployeeAssignmentDto both satisfy it without a common subclass carrying the fields.
 */
export type AssignmentRowSource = AssignmentBaseDto & {
  assignmentTypeCategory?: string;
  assignmentTypeCategoryName?: string;
  practiceAreas?: PracticeAreaDto[];
};

/**
 * The one DTO-to-row mapping for every host of the assignments card. The mode-specific
 * fields — who the row is for (employeeId, fullName, title) or what it is on
 * (clientMatterNumber, clientName, matterTitle), the dialog title, the resource name —
 * come from the caller as `extras`, which win over anything mapped here.
 *
 * Shared so the four places that build rows cannot drift. Two details matter enough to
 * name. A missing note lands on the row as `null`, never undefined: the edit dialog reads
 * undefined as "untouched", so a row that left it unset would make a saved note look like
 * no save. And canViewResource is the DTO's own, never forced true — even on the
 * dashboard, where every row is the viewer's: the server can report hasManagerNote with
 * the note withheld when the viewer holds ManagerNotes.Read but the resource is outside
 * their visibility scope, which fails closed on a missing office code and so can happen on
 * one's own row. Forcing true made the dialog show an empty disabled note box instead of
 * hiding the section, implying no note exists.
 *
 * The card also patches a synthetic row from a create response through this mapping, so
 * what the server says the row holds — its request id, its notes — is what the row shows,
 * rather than whatever the card assumed a brand-new row would have.
 */
export function toAssignmentRow(
  dto: AssignmentRowSource,
  extras: Partial<AssignmentRow> = {},
): AssignmentRow {
  return {
    requestId: dto.requestId,
    assignedResourceId: dto.assignedResourceId,
    status: dto.status,
    statusName: dto.statusName,
    startDate: dto.startDate,
    endDate: dto.endDate,
    lastBilledDate: dto.lastBilledDate,
    assignmentTypeCategory: dto.assignmentTypeCategory ?? '',
    assignmentTypeCategoryName: dto.assignmentTypeCategoryName ?? '',
    hasNotes: !!dto.hasManagerNote,
    notesInfo: dto.managerNotesInfo ?? null,
    hasResourceNotes: !!dto.hasResourceNote,
    resourceNotesInfo: dto.resourceNotesInfo ?? null,
    canViewResource: dto.canViewResource,
    canViewRequest: dto.canViewRequest,
    practiceAreas: dto.practiceAreas,
    taskAssignments: dto.taskAssignments,
    ...extras,
  };
}

/**
 * Carries a row's in-place edits back onto the assignment it was built from: the notes
 * (the reverse of the four note lines in toAssignmentRow) and the assignment type, which
 * the edit dialog also changes and the Assignment Notes card shows as the Primary/Coverage
 * qualifier in matter mode. Kept beside toAssignmentRow so the two mappings cannot drift.
 *
 * The assignments card edits its rows in place and never touches the host's DTOs, so a
 * host that also renders those DTOs elsewhere (the Assignment Notes card) would show the
 * pre-edit values until a reload. Returns a new list with a new object for the matched
 * assignment, so a signal input bound to the list sees the change; the other assignments
 * keep their references. A row that matches nothing (or has no assignment id) returns the
 * list as given, since there is nothing to re-render.
 */
export function applyAssignmentRowEdits<T extends AssignmentRowSource>(
  assignments: T[],
  row: AssignmentRow,
): T[] {
  const id = row.assignedResourceId;
  if (id == null || !assignments.some((a) => a.assignedResourceId === id)) {
    return assignments;
  }
  return assignments.map((a) =>
    a.assignedResourceId === id
      ? {
          ...a,
          hasManagerNote: !!row.hasNotes,
          managerNotesInfo: row.notesInfo ?? undefined,
          hasResourceNote: !!row.hasResourceNotes,
          resourceNotesInfo: row.resourceNotesInfo ?? undefined,
          assignmentTypeCategory: row.assignmentTypeCategory,
          assignmentTypeCategoryName: row.assignmentTypeCategoryName,
        }
      : a,
  );
}
